import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Dialog, Loader, Tab, TabView, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts/auth';
import { SelectedLocationContext } from 'contexts/selected-location';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { useInvoiceApi } from 'hooks/use-invoice-api';
import { setInvoiceStatusReasons, setInvoiceStatuses } from 'services';
import { Customer, InvoiceStatus, InvoiceSummary, Role } from 'types';
import { FinalizedInvoiceList } from './finalized-invoice-list';
import { GenerateInvoicesDialog } from './generate-invoices-dialog';
import { PendingInvoiceList } from './pending-invoice-list';

export const StorageInvoices: FC = () => {
  const { selectedLocation } = useContext(SelectedLocationContext);
  const { getAllCustomers } = useCustomerAPI();
  const { userProfile } = useAuth();
  const { ref: holdDialogRef, openDialog: openHoldDialog, closeDialog: closeHoldDialog } = useDialog({});

  const [selectedPendingInvoiceIds, setSelectedPendingInvoiceIds] = useState<number[]>([]);
  const [selectedFinalizedInvoiceIds, setSelectedFinalizedInvoiceIds] = useState<number[]>([]);
  const [pendingInvoiceTabKey, setPendingInvoiceTabKey] = useState(Math.random());
  const [finalizedInvoiceTabKey, setFinalizedInvoiceTabKey] = useState(Math.random());
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
  const [finalizeInvoicesLoading, setFinalizeInvoicesLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadPdfsLoading, setDownloadPdfsLoading] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  const { ref: deleteDialogRef, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog({});

  const { bulkDeleteInvoices, finalizeInvoicesForLocation, bulkDownloadPdfs } = useInvoiceApi();

  // CALLBACKS
  const generateInvoices = useCallback(() => {
    setOpenGenerateDialog(true);
  }, []);

  // const pendingRowSelectionChange = useCallback((rowSelection: GridRowSelectionModel) => {
  //   setSelectedPendingInvoiceIds(rowSelection.map(idString => Number(idString)).filter(id => !isNaN(id)));
  // }, []);

  const pendingRowSelectionChange = useCallback((selectedRows: InvoiceSummary[]) => {
    setSelectedPendingInvoiceIds(selectedRows.map(i => i.id));
  }, []);

  // const finalizedRowSelectionChange = useCallback((rowSelection: GridRowSelectionModel) => {
  //   setSelectedFinalizedInvoiceIds(rowSelection.map(idString => Number(idString)).filter(id => !isNaN(id)));
  // }, []);

  const finalizedRowSelectionChange = useCallback((selectedRows: InvoiceSummary[]) => {
    setSelectedFinalizedInvoiceIds(selectedRows.map(i => i.id));
  }, []);

  const setStatuses = useCallback(
    (status: InvoiceStatus) => {
      setUpdateStatusLoading(true);
      setInvoiceStatuses({ invoiceIds: selectedPendingInvoiceIds, status })
        .then(async invoices => {
          if (invoices && invoices[0] && holdReason) {
            return await setInvoiceStatusReasons({ invoiceIds: selectedPendingInvoiceIds, statusReason: holdReason });
          } else return invoices;
        })
        .then(invoices => {
          if (invoices !== null) {
            // Changing the key re-renders the tab which reloads the list
            setPendingInvoiceTabKey(Math.random());
            pendingRowSelectionChange([]);
          }
        })
        .finally(() => setUpdateStatusLoading(false));
    },
    [pendingRowSelectionChange, selectedPendingInvoiceIds, holdReason]
  );

  const finalizeApproved = useCallback(() => {
    if (selectedLocation?.id === undefined) {
      return;
    }
    setFinalizeInvoicesLoading(true);
    finalizeInvoicesForLocation(selectedLocation.id)
      .then(() => {
        setFinalizedInvoiceTabKey(Math.random());
        setPendingInvoiceTabKey(Math.random());
      })
      .finally(() => setFinalizeInvoicesLoading(false));
  }, [selectedLocation?.id, finalizeInvoicesForLocation]);

  const bulkDelete = useCallback(() => {
    const ids = activeTabIndex === 0 ? selectedPendingInvoiceIds : selectedFinalizedInvoiceIds;
    setDeleteLoading(true);
    bulkDeleteInvoices(ids)
      .then(() => {})
      .finally(() => {
        if (activeTabIndex === 0) {
          setPendingInvoiceTabKey(Math.random());
        } else {
          setFinalizedInvoiceTabKey(Math.random());
        }
        setDeleteLoading(false);
        closeDeleteDialog();
      });
  }, [activeTabIndex, closeDeleteDialog, selectedFinalizedInvoiceIds, selectedPendingInvoiceIds, bulkDeleteInvoices]);

  const downloadSelectedPdfs = useCallback(() => {
    if (!selectedFinalizedInvoiceIds.length) {
      return;
    }
    setDownloadPdfsLoading(true);
    bulkDownloadPdfs(selectedFinalizedInvoiceIds).finally(() => setDownloadPdfsLoading(false));
  }, [bulkDownloadPdfs, selectedFinalizedInvoiceIds]);

  // EFFECTS
  // Load Customers
  useEffect(() => {
    if (selectedLocation?.id) {
      getAllCustomers(selectedLocation.id, false).then(customers => {
        if (!!customers) {
          setCustomers(customers.sort((a, b) => a.name?.localeCompare(b.name, undefined, { sensitivity: 'base' })));
        }
      });
    }
  }, [getAllCustomers, selectedLocation?.id]);

  const tabs: Tab[] = useMemo(
    () => [
      ...Array(Number([Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING].includes(userProfile.role))).fill({
        label: 'Pending Invoices',
        content: <PendingInvoiceList key={pendingInvoiceTabKey} rowSelectionChange={pendingRowSelectionChange} />
      }),
      {
        label: 'Finalized Invoices',
        content: <FinalizedInvoiceList key={finalizedInvoiceTabKey} rowSelectionChange={finalizedRowSelectionChange} />
      }
    ],
    [
      finalizedInvoiceTabKey,
      finalizedRowSelectionChange,
      pendingInvoiceTabKey,
      pendingRowSelectionChange,
      userProfile.role
    ]
  );

  return (
    <>
      <Dialog ref={deleteDialogRef} closeDialog={closeDeleteDialog} data-cy="storage-invoices-delete-dialog">
        <div className="dialog-header">
          <h3>Delete Invoices?</h3>
        </div>
        <div className="dialog-body">
          <p>Are you sure you want to delete these invoices?</p>
        </div>
        <div className="dialog-footer g-2">
          <button className="btn-outline">Cancel</button>
          <Loader loading={deleteLoading}>
            <button className="btn-danger" onClick={bulkDelete}>
              <span>Delete</span>
            </button>
          </Loader>
        </div>
      </Dialog>
      <Dialog ref={holdDialogRef} closeDialog={closeHoldDialog} data-cy="storage-invoices-hold-dialog">
        <div className="dialog-header">
          <h3>Hold Invoice</h3>
        </div>
        <div className="dialog-body">
          <p className="margin-bottom-16px">Why are you holding this invoice?</p>
          <input type="text" value={holdReason} onChange={({ target }) => setHoldReason(target.value)} />
        </div>
        <div className="dialog-footer g-2">
          <button className="btn-outline" onClick={closeHoldDialog}>
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              setStatuses(InvoiceStatus.HOLD);
              closeHoldDialog();
            }}
          >
            <span>Hold</span>
          </button>
        </div>
      </Dialog>
      <GenerateInvoicesDialog
        mode="generate"
        onSuccess={() => {
          setPendingInvoiceTabKey(Math.random());
        }}
        open={openGenerateDialog}
        close={() => setOpenGenerateDialog(false)}
        customers={customers}
      />
      {/* A preview creates no invoices, so it never reloads the pending list. */}
      <GenerateInvoicesDialog
        mode="preview"
        open={openPreviewDialog}
        close={() => setOpenPreviewDialog(false)}
        customers={customers}
      />
      <div className="page">
        <div className="page-header">
          <h1 className="text-uppercase">Storage Invoices</h1>
          <div className="d-flex g-3">
            {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING].includes(userProfile.role) && activeTabIndex === 0 && (
              <>
                {!!selectedPendingInvoiceIds.length && (
                  <>
                    <button className="btn-danger" onClick={openDeleteDialog} data-cy="storage-invoices-delete-button">
                      <div className="d-flex align-items-center g-2">
                        <span>Delete</span>
                      </div>
                    </button>
                    <Loader loading={updateStatusLoading} scale="50%">
                      <button className="btn-outline" onClick={openHoldDialog} data-cy="storage-invoices-hold-button">
                        <div className="d-flex align-items-center g-2">
                          <span>Hold</span>
                        </div>
                      </button>
                    </Loader>
                    <Loader loading={updateStatusLoading} scale="50%">
                      <button
                        className="btn-primary"
                        onClick={() => setStatuses(InvoiceStatus.APPROVED)}
                        data-cy="storage-invoices-approve-button"
                      >
                        <div className="d-flex align-items-center g-2">
                          <span>Approve</span>
                        </div>
                      </button>
                    </Loader>
                  </>
                )}
                <button
                  className="btn-outline"
                  onClick={() => generateInvoices()}
                  data-cy="storage-invoices-generate-button"
                >
                  <div className="d-flex align-items-center g-2">
                    <span>GENERATE INVOICES</span>
                  </div>
                </button>
                <button
                  className="btn-outline"
                  onClick={() => setOpenPreviewDialog(true)}
                  data-cy="storage-invoices-preview-button"
                >
                  <div className="d-flex align-items-center g-2">
                    <span>GENERATE PREVIEW</span>
                  </div>
                </button>
                <Loader loading={finalizeInvoicesLoading} scale="50%">
                  <button className="btn-primary" onClick={finalizeApproved} data-cy="storage-invoices-finalize-button">
                    <div className="d-flex align-items-center g-2">
                      <span>INVOICE APPROVED CUSTOMERS</span>
                    </div>
                  </button>
                </Loader>
              </>
            )}
            {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING].includes(userProfile.role) && activeTabIndex === 1 && (
              <>
                {!!selectedFinalizedInvoiceIds.length && (
                  <>
                    {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
                      <Loader loading={downloadPdfsLoading} scale="50%">
                        <button
                          className="btn-primary"
                          onClick={downloadSelectedPdfs}
                          data-cy="storage-invoices-download-pdfs-button"
                        >
                          <div className="d-flex align-items-center g-2">
                            <span>Download PDFs ({selectedFinalizedInvoiceIds.length})</span>
                          </div>
                        </button>
                      </Loader>
                    )}
                    <button
                      className="btn-danger"
                      onClick={openDeleteDialog}
                      data-cy="storage-invoices-delete-finalized-button"
                    >
                      <div className="d-flex align-items-center g-2">
                        <span>Delete</span>
                      </div>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="page-content flex-grow-1">
          <TabView
            tabs={tabs}
            onTabSwitch={(index: number) => {
              setActiveTabIndex(index);
            }}
          />
        </div>
      </div>
      <SuccessDialog />
      <ErrorDialog />
    </>
  );
};
